import React, { useState } from 'react';
import { FaExclamationTriangle, FaUser, FaBook, FaClock, FaDoorOpen } from 'react-icons/fa';

const ConflictApprovalModal = ({
  isOpen,
  conflicts,
  onApprove,
  onReject,
  onClose,
  loading
}) => {
  const [expandedConflicts, setExpandedConflicts] = useState({});

  if (!isOpen) return null;

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'soft': return {
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        text: 'text-yellow-800',
        icon: 'text-yellow-600',
        badge: 'bg-yellow-100 text-yellow-800'
      };
      case 'medium': return {
        bg: 'bg-orange-50',
        border: 'border-orange-200',
        text: 'text-orange-800',
        icon: 'text-orange-600',
        badge: 'bg-orange-100 text-orange-800'
      };
      default: return {
        bg: 'bg-red-50',
        border: 'border-red-200',
        text: 'text-red-800',
        icon: 'text-red-600',
        badge: 'bg-red-100 text-red-800'
      };
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'soft': return '⚠️';
      case 'medium': return '🔶';
      default: return '🔴';
    }
  };

  const toggleExpand = (conflictId) => {
    setExpandedConflicts(prev => ({
      ...prev,
      [conflictId]: !prev[conflictId]
    }));
  };

  const formatTime = (timeSlot) => {
    if (!timeSlot) return 'Unknown time';
    return `${timeSlot.start} - ${timeSlot.end}`;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div className="inline-block w-full max-w-3xl px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:p-6">
          <div className="sm:flex sm:items-start">
            <div className="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-yellow-100 rounded-full sm:mx-0 sm:h-10 sm:w-10">
              <FaExclamationTriangle className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                Faculty Conflicts Detected
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  The following scheduling conflicts were detected. You can approve them if they are intentional (e.g., combined lectures, makeup classes), or go back and resolve them.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 max-h-96 overflow-y-auto space-y-4">
            {conflicts.map((conflict, idx) => {
              const severityColors = getSeverityColor(conflict.severity);
              const conflictId = `conflict-${idx}`;
              const isExpanded = expandedConflicts[conflictId];
              
              return (
                <div 
                  key={idx} 
                  className={`rounded-lg border ${severityColors.border} ${severityColors.bg} transition-all duration-200`}
                >
                  {/* Conflict Header */}
                  <div 
                    className="p-4 cursor-pointer hover:bg-opacity-80 transition-colors"
                    onClick={() => toggleExpand(conflictId)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1">
                        <span className="text-xl">{getSeverityIcon(conflict.severity)}</span>
                        <div className="flex-1">
                          <div className="flex items-center flex-wrap gap-2 mb-1">
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${severityColors.badge}`}>
                              {conflict.severity.toUpperCase()} CONFLICT
                            </span>
                            {conflict.type === 'room' && (
                              <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">
                                Room Conflict
                              </span>
                            )}
                            {conflict.type === 'cross-class' && (
                              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                                Cross-Class Conflict
                              </span>
                            )}
                          </div>
                          <p className={`text-sm font-medium ${severityColors.text}`}>
                            {conflict.message}
                          </p>
                          {conflict.facultyName && (
                            <div className="flex items-center mt-2 text-xs text-gray-600">
                              <FaUser className="mr-1" size={10} />
                              <span>Faculty: <strong>{conflict.facultyName}</strong></span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-gray-400">
                        <svg className={`w-5 h-5 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="px-4 pb-4 pt-0 border-t border-gray-200 mt-2">
                      {/* New Entry Details */}
                      {conflict.newEntry && (
                        <div className="mb-4">
                          <h4 className="text-xs font-bold text-gray-700 uppercase mb-2">New Session</h4>
                          <div className="bg-white rounded-lg p-3 space-y-2">
                            <div className="flex items-center text-sm">
                              <FaClock className="text-gray-400 mr-2" size={12} />
                              <span className="text-gray-700">
                                {conflict.newEntry.dayOfWeek} • {formatTime(conflict.newEntry.timeSlot)}
                              </span>
                            </div>
                            {conflict.newEntry.subjectName && (
                              <div className="flex items-center text-sm">
                                <FaBook className="text-gray-400 mr-2" size={12} />
                                <span className="text-gray-700">
                                  Subject: <strong>{conflict.newEntry.subjectName}</strong>
                                </span>
                              </div>
                            )}
                            {conflict.newEntry.room && (
                              <div className="flex items-center text-sm">
                                <FaDoorOpen className="text-gray-400 mr-2" size={12} />
                                <span className="text-gray-700">Room: {conflict.newEntry.room}</span>
                              </div>
                            )}
                            {conflict.newEntry.facultyName && (
                              <div className="flex items-center text-sm">
                                <FaUser className="text-gray-400 mr-2" size={12} />
                                <span className="text-gray-700">Faculty: {conflict.newEntry.facultyName}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Existing Entry Details */}
                      {conflict.existingEntry && (
                        <div className="mb-4">
                          <h4 className="text-xs font-bold text-gray-700 uppercase mb-2">Existing Session</h4>
                          <div className="bg-white rounded-lg p-3 space-y-2">
                            <div className="flex items-center text-sm">
                              <FaClock className="text-gray-400 mr-2" size={12} />
                              <span className="text-gray-700">
                                {conflict.existingEntry.dayOfWeek} • {formatTime(conflict.existingEntry.timeSlot)}
                              </span>
                            </div>
                            {conflict.existingEntry.className && (
                              <div className="flex items-center text-sm">
                                <span className="text-gray-400 mr-2">🏫</span>
                                <span className="text-gray-700">
                                  Class: <strong>{conflict.existingEntry.className}</strong>
                                </span>
                              </div>
                            )}
                            {conflict.existingEntry.subjectName && (
                              <div className="flex items-center text-sm">
                                <FaBook className="text-gray-400 mr-2" size={12} />
                                <span className="text-gray-700">
                                  Subject: <strong>{conflict.existingEntry.subjectName}</strong>
                                </span>
                              </div>
                            )}
                            {conflict.existingEntry.facultyName && (
                              <div className="flex items-center text-sm">
                                <FaUser className="text-gray-400 mr-2" size={12} />
                                <span className="text-gray-700">Faculty: {conflict.existingEntry.facultyName}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Multiple Entries (for within-faculty conflicts) */}
                      {conflict.entries && conflict.entries.length > 0 && !conflict.existingEntry && (
                        <div>
                          <h4 className="text-xs font-bold text-gray-700 uppercase mb-2">Conflicting Sessions</h4>
                          <div className="space-y-2">
                            {conflict.entries.map((entry, eIdx) => (
                              <div key={eIdx} className="bg-white rounded-lg p-3">
                                <div className="flex items-center text-sm mb-1">
                                  <FaClock className="text-gray-400 mr-2" size={12} />
                                  <span className="text-gray-700">
                                    {entry.dayOfWeek} • {formatTime(entry.timeSlot)}
                                  </span>
                                </div>
                                {entry.subjectName && (
                                  <div className="flex items-center text-sm">
                                    <FaBook className="text-gray-400 mr-2" size={12} />
                                    <span className="text-gray-700">Subject: {entry.subjectName}</span>
                                  </div>
                                )}
                                {entry.facultyName && (
                                  <div className="flex items-center text-sm mt-1">
                                    <FaUser className="text-gray-400 mr-2" size={12} />
                                    <span className="text-gray-700">Faculty: {entry.facultyName}</span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Room Conflict Details */}
                      {conflict.type === 'room' && (
                        <div className="mt-2 p-2 bg-gray-50 rounded">
                          <p className="text-xs text-gray-600">
                            <strong>Room {conflict.room}</strong> has been scheduled for multiple sessions at the same time.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-6 sm:flex sm:flex-row-reverse gap-3">
            <button
              type="button"
              onClick={() => onApprove('Approved by user')}
              disabled={loading}
              className="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : (
                '✓ Approve All Conflicts'
              )}
            </button>
            <button
              type="button"
              onClick={onReject}
              disabled={loading}
              className="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
            >
              ← Go Back and Resolve
            </button>
          </div>

          <div className="mt-4 text-xs text-gray-400 text-center">
            <p>⚠️ Approving conflicts will record this decision for audit purposes. Please ensure these conflicts are intentional.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConflictApprovalModal;